import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import api from '../../services/api';

const OfferModal = ({ show, onHide, recipient, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [terms, setTerms] = useState([]);
  const [formData, setFormData] = useState({
    activity: '',
    terms: '',
  });

  // Fetch lookups for activities and terms
  useEffect(() => {
    const fetchLookups = async () => {
      setLoadingLookups(true);
      setError(null);

      try {
        // Fetch lookups from the API
        const response = await api.get('/lookups');

        if (response.data && response.data.data) {
          // Find the offer_activities and offer_terms lookups
          const activitiesLookup = response.data.data.find(lookup => lookup.name === 'offer_activities');
          const termsLookup = response.data.data.find(lookup => lookup.name === 'offer_terms');

          // Set the activities and terms if found
          if (activitiesLookup && activitiesLookup.choices) {
            setActivities(activitiesLookup.choices);
          } else {
            console.warn('offer_activities lookup not found in API response');
            setActivities([]);
          }

          if (termsLookup && termsLookup.choices) {
            setTerms(termsLookup.choices);
          } else {
            console.warn('offer_terms lookup not found in API response');
            setTerms([]);
          }
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (error) {
        console.error('Error fetching lookups:', error);
        setError('Failed to load offer options. Please try again.');
        setActivities([]);
        setTerms([]);
      } finally {
        setLoadingLookups(false);
      }
    };

    if (show) {
      fetchLookups();
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Create FormData directly
      const formDataObj = new FormData();
      formDataObj.append('recipient', recipient);
      formDataObj.append('message_type', 2); // Offer type
      formDataObj.append('activity', formData.activity);
      formDataObj.append('terms', formData.terms);

      // Send the offer using the POST /messages endpoint with FormData
      const response = await api.post('/messages', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 201) {
        // Success - close modal and notify parent component
        onSuccess(response.data.message);
        handleClose();
      } else {
        setError('Failed to send offer. Please try again.');
      }
    } catch (error) {
      console.error('Error sending offer:', error);
      setError(error.response?.data?.message || 'Failed to send offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    setFormData({
      activity: '',
      terms: '',
    });
    setError(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Make a Date Offer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {loadingLookups ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading offer options...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Activity</Form.Label>
              <Form.Select
                name="activity"
                value={formData.activity}
                onChange={handleChange}
                required
              >
                <option value="">Select an activity</option>
                {activities.map(activity => (
                  <option key={activity.id} value={activity.id}>
                    {activity.value}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Terms</Form.Label>
              <Form.Select
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                required
              >
                <option value="">Select terms</option>
                {terms.map(term => (
                  <option key={term.id} value={term.id}>
                    {term.value}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>


          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || loadingLookups || !formData.activity || !formData.terms}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Sending...
            </>
          ) : (
            'Send Offer'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OfferModal;
